/**
 * Highlight colour helpers
 *
 * The default `==highlight==` is classic marker yellow, strong and opaque, with
 * the highlighted text switched to dark ink (MARKER_INK, ~13:1 on the band), on
 * any palette.
 *
 * An authored hex (`==phrase=={#hex}`) keeps the narrative's white text, so the
 * band has to stay translucent. White text loses contrast on any band lighter
 * than its backdrop, and many palettes are already under 4.5:1 on their
 * brighter stops, so an absolute floor is not reachable. The rule is relative:
 * use the most opaque band (at most 65%, never fainter than 18%) that keeps the
 * worst-case contrast across the palette's stops at 4.5:1, or within 10% of
 * what the bare backdrop already gives when that is lower. Light hues (yellow,
 * white) end up fainter than saturated mid-tones.
 *
 * An author who wants full control appends alpha to the hex (`{#FFD400B3}`):
 * an explicit alpha is honoured as written, guard bypassed.
 *
 * The guard assumes white text over the section's palette, the combination the
 * authoring guidance prescribes. Text colour overrides and image/video
 * backgrounds are not modelled.
 */

import type { ColorPalette } from "../types/narrative";

interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** `a` is 0–1 when the hex carried alpha (#RGBA / #RRGGBBAA). */
interface RgbaFromHex extends Rgb {
  a?: number;
}

const WHITE: Rgb = { r: 255, g: 255, b: 255 };

/** The default band: classic highlighter yellow. */
const DEFAULT_MARKER = "rgba(255, 225, 77, 0.92)";

/** Text colour inside a default (yellow) highlight. Not for authored hexes. */
export const MARKER_INK = "#1a1a2e";

/** Most opaque a guarded (authored) band gets. */
const MAX_ALPHA_PCT = 65;
/** The faintest we fall back to. */
const MIN_ALPHA_PCT = 18;

/** WCAG AA for normal text, and the share of the backdrop's own contrast to keep. */
const TARGET_CONTRAST = 4.5;
const KEEP_BACKDROP_CONTRAST = 0.9;

const HEX_RE = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

/** Parse `#RGB`, `#RGBA`, `#RRGGBB` or `#RRGGBBAA`; null for anything else. */
export function parseHex(input: string | undefined): RgbaFromHex | null {
  if (!input || !HEX_RE.test(input.trim())) return null;
  let hex = input.trim().slice(1);
  if (hex.length <= 4) {
    hex = Array.from(hex, (c) => c + c).join("");
  }
  const channel = (i: number) => parseInt(hex.slice(i, i + 2), 16);
  const rgb: RgbaFromHex = { r: channel(0), g: channel(2), b: channel(4) };
  if (hex.length === 8) {
    rgb.a = Math.round((channel(6) / 255) * 100) / 100;
  }
  return rgb;
}

function relativeLuminance({ r, g, b }: Rgb): number {
  const linear = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
}

/** WCAG contrast ratio between two opaque colours. */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort(
    (x, y) => y - x,
  );
  return (hi + 0.05) / (lo + 0.05);
}

/** Blend `over` onto `under` at `alpha`, the way CSS composites in sRGB. */
function blend(under: Rgb, over: Rgb, alpha: number): Rgb {
  const mix = (u: number, o: number) => u * (1 - alpha) + o * alpha;
  return {
    r: mix(under.r, over.r),
    g: mix(under.g, over.g),
    b: mix(under.b, over.b),
  };
}

function paletteStops(palette: ColorPalette | undefined): Rgb[] {
  if (!palette) return [];
  return [palette.from, palette.via, palette.to]
    .map((stop) => parseHex(stop))
    .filter((stop): stop is RgbaFromHex => stop !== null);
}

/**
 * Most opaque alpha (0–1) at which white text keeps its
 * contrast across every stop of the palette. With no parseable stops there is
 * nothing to check against, so a middling default is returned.
 */
export function guardedAlpha(color: Rgb, stops: Rgb[]): number {
  if (stops.length === 0) return 0.3;
  const bare = Math.min(...stops.map((stop) => contrastRatio(WHITE, stop)));
  const target = Math.min(TARGET_CONTRAST, KEEP_BACKDROP_CONTRAST * bare);
  for (let pct = MAX_ALPHA_PCT; pct > MIN_ALPHA_PCT; pct--) {
    const alpha = pct / 100;
    const worst = Math.min(
      ...stops.map((stop) => contrastRatio(WHITE, blend(stop, color, alpha))),
    );
    if (worst >= target) return alpha;
  }
  return MIN_ALPHA_PCT / 100;
}

function toRgba({ r, g, b }: Rgb, alpha: number): string {
  return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${alpha})`;
}

/**
 * CSS colour for a highlight band.
 *
 * @param color   Hex from the `{#hex}` suffix; undefined for the default.
 * @param palette The section's effective palette: the backdrop an authored
 *   colour is checked against. Unused for the default.
 */
export function resolveMarkerColor(
  color: string | undefined,
  palette: ColorPalette | undefined,
): string {
  const explicit = parseHex(color);
  if (explicit) {
    // Alpha written into the hex is the author's call; leave the guard out.
    const alpha = explicit.a ?? guardedAlpha(explicit, paletteStops(palette));
    return toRgba(explicit, alpha);
  }
  return DEFAULT_MARKER;
}
